/**
 * Image optimization utilities for artwork display images
 * NOTE: These optimizations are ONLY for display images, NOT for print files (print_file_url)
 */

interface ImageSrcSetOptions {
  url: string;
  sizes?: number[];
  quality?: number;
}

/**
 * Check if URL is a print file (should NOT be optimized)
 * Print files are stored in print_file_url and need full quality
 * CRITICAL: This must be checked FIRST before any optimization
 */
export function isPrintFile(url: string): boolean {
  // Safety check for undefined/null/empty URLs
  if (!url || typeof url !== 'string') {
    return false;
  }
  
  // Check for printful file patterns - these files must NEVER be optimized
  if (url.includes('printful-') || url.includes('print-file-')) {
    return true;
  }
  
  // Additional safety check: if URL contains very high resolution indicators
  // it's likely a print file
  if (url.includes('highres') || url.includes('print-ready')) {
    return true;
  }
  
  return false;
}

/**
 * Generates srcset for responsive images using Supabase Storage transformations
 * Only use for display images, never for print_file_url URLs
 */
export function generateImageSrcSet({ 
  url, 
  sizes = [400, 800, 1200], 
  quality = 75 
}: ImageSrcSetOptions): string {
  // Safety check for undefined/null/empty URLs
  if (!url || typeof url !== 'string') {
    return '';
  }
  
  // CRITICAL: Never optimize print files
  if (isPrintFile(url)) {
    return '';
  }

  // Don't optimize external URLs or non-Supabase URLs
  if (!url.includes('supabase')) {
    return '';
  }

  return sizes
    .map(size => {
      const optimizedUrl = `${url}?width=${size}&quality=${quality}`;
      return `${optimizedUrl} ${size}w`;
    })
    .join(', ');
}

/**
 * Generates sizes attribute for responsive images
 */
export function generateImageSizes(maxWidth: string = '100vw'): string {
  return `(max-width: 640px) 50vw, (max-width: 1024px) 33vw, ${maxWidth}`;
}

/**
 * Generate optimized URL with specific dimensions
 * CRITICAL: Never used for print files
 */
export function getOptimizedUrl(url: string, width: number, quality: number = 75): string {
  // Safety check for undefined/null/empty URLs
  if (!url || typeof url !== 'string') {
    return '';
  }
  
  // CRITICAL: Never optimize print files
  if (isPrintFile(url)) {
    return url;
  }

  if (!url.includes('supabase')) {
    return url;
  }

  return `${url}?width=${width}&quality=${quality}`;
}

/**
 * Generate blur placeholder for progressive loading
 */
export function getBlurPlaceholder(url: string): string {
  // Safety check for undefined/null/empty URLs
  if (!url || typeof url !== 'string') {
    return '';
  }
  
  // CRITICAL: Never optimize print files
  if (isPrintFile(url)) {
    return url;
  }

  if (!url.includes('supabase')) {
    return url;
  }

  // Very small, low quality for blur-up effect
  return `${url}?width=20&quality=10`;
}
